// Left navigation sidebar — mirrors the legacy Sidebar() component.
// Shows nav items, current user, logout, and service/AI health indicators.

import { clsx } from "clsx";
import { useLocation, useNavigate } from "react-router-dom";
import { AppLangSwitcher } from "@/components/ui/AppLangSwitcher";
import { ThemeToggle } from "@/components/ui/ThemeToggle";
import { useDict } from "@/i18n/LocaleProvider";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { useAiStatus, useHealthServices } from "@/hooks/useDashboard";

type NavKey =
  | "dashboard"
  | "engagements"
  | "history"
  | "profile"
  | "upgrade"
  | "admin";

interface NavItem {
  path: string;
  key: NavKey;
  icon: string;
  badge?: boolean;
  superuserOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: "/app", key: "dashboard", icon: "⊞" },
  { path: "/app/engagements", key: "engagements", icon: "◇" },
  { path: "/app/history", key: "history", icon: "⏱" },
  { path: "/app/profile", key: "profile", icon: "◉" },
  { path: "/app/upgrade", key: "upgrade", icon: "★" },
  { path: "/app/admin", key: "admin", icon: "⚙", badge: true, superuserOnly: true },
];

function isActive(currentPath: string, itemPath: string): boolean {
  if (itemPath === "/app") return currentPath === "/app" || currentPath === "/app/";
  return currentPath === itemPath || currentPath.startsWith(itemPath + "/");
}

export function Sidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useDict().app;
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isSidebarOpen = useUiStore((s) => s.isSidebarOpen);
  const closeSidebar = useUiStore((s) => s.closeSidebar);

  const { data: health } = useHealthServices();
  const { data: aiStatus } = useAiStatus();

  const items = NAV_ITEMS.filter((i) => !i.superuserOnly || user?.is_superuser);

  function handleLogout() {
    logout();
    // Hard redirect to the public home. Logout fires from inside /app
    // (guarded by RequireAuth); a soft navigate would race RequireAuth's
    // own <Navigate to="/login">. A full reload leaves /app cleanly and
    // lands on the home with a fresh, logged-out state.
    window.location.assign("/");
  }

  return (
    <aside
      className={clsx(
        "w-[220px] bg-bg2 border-r border-stroke flex flex-col",
        "fixed h-screen left-0 top-0 z-40",
        // Mobile: slide in/out based on isSidebarOpen.
        // md+: always visible, transform reset.
        "transition-transform duration-200 ease-out md:translate-x-0",
        isSidebarOpen ? "translate-x-0" : "-translate-x-full",
      )}
    >
      {/* Logo header */}
      <div
        className="px-4 pt-5 pb-4 border-b border-stroke cursor-pointer flex items-center justify-between gap-2"
        onClick={() => navigate("/app")}
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Logo size matches the landing page nav (h-9 sm:h-10). The
              h-14 from the legacy layout was too tall and caused the
              wordmark + tagline group to overflow the 220px drawer
              behind the close button on mobile. */}
          <img src="/logo.png" alt="TrueHacking" className="h-9 sm:h-10 w-auto" />
          {/* Wordmark + tagline only on persistent-sidebar widths (md+).
              In mobile drawer mode the logo image alone is enough — same
              pattern as the landing nav, and avoids the close button
              overlap that this used to produce. */}
          <div className="hidden md:block min-w-0">
            <div className="font-display text-[18px] font-bold text-accent tracking-[0.12em] leading-none truncate">
              TrueHacking
            </div>
            <div className="font-mono text-[8px] text-dim tracking-[0.08em] mt-[2px] truncate">
              AI PENTEST COPILOT
            </div>
          </div>
        </div>
        {/* Mobile close (X) — clicking the logo also closes by virtue of nav */}
        <button
          type="button"
          aria-label={t.closeMenu}
          onClick={(e) => {
            e.stopPropagation();
            closeSidebar();
          }}
          className="md:hidden w-9 h-9 flex items-center justify-center text-muted hover:text-content"
        >
          <span className="text-[18px] leading-none">✕</span>
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 pt-4 overflow-auto">
        {items.map((item) => {
          const active = isActive(location.pathname, item.path);
          return (
            <button
              key={item.path}
              onClick={() => {
                navigate(item.path);
                closeSidebar();
              }}
              className={clsx(
                "flex items-center gap-3 w-full px-3 py-[10px] mb-1 border rounded min-h-[44px]",
                "font-mono text-[12px] font-semibold text-left transition-all duration-150 tracking-[0.02em] cursor-pointer",
                active
                  ? "bg-accent-bg border-accent/40 text-accent"
                  : "bg-transparent border-transparent text-muted hover:border-stroke",
              )}
            >
              <span className="text-base w-5 text-center">{item.icon}</span>
              <span className="flex-1">{t.nav[item.key]}</span>
              {item.badge && (
                <span className="px-[6px] py-[2px] bg-danger/10 border border-danger/40 rounded-[3px] text-danger text-[8px] font-bold tracking-[0.05em]">
                  {t.adminBadge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* User info + logout */}
      {user && (
        <div className="px-3 py-[10px] border-t border-stroke bg-bg3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-full bg-accent flex items-center justify-center font-display text-[12px] font-bold text-white shrink-0">
              {(user.full_name || user.email).charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <div className="font-mono text-[10px] text-content font-semibold truncate">
                {user.full_name || user.email}
              </div>
              {user.organizations[0] && (
                <div className="font-mono text-[9px] text-accent mt-[1px] uppercase tracking-[0.05em]">
                  {user.organizations[0].role} · {user.organizations[0].name}
                </div>
              )}
            </div>
            <ThemeToggle className="w-8 h-8" />
          </div>
          <div className="flex items-center justify-center mb-2">
            <AppLangSwitcher />
          </div>
          <button
            onClick={handleLogout}
            className="w-full py-2 md:py-[5px] min-h-[36px] md:min-h-0 bg-transparent border border-stroke rounded text-muted font-mono text-[9px] cursor-pointer uppercase tracking-[0.05em] hover:text-content hover:border-stroke-hv transition-colors"
          >
            ⎋ {t.logout}
          </button>
        </div>
      )}

      {/* Tagline + Status footer */}
      <div className="px-4 py-[10px] border-t border-stroke">
        <div className="font-mono text-[9px] text-dim tracking-[0.05em] mb-2 text-center italic whitespace-pre-line">
          {t.tagline}
        </div>
        <div className="flex flex-col gap-1">
          {aiStatus && (
            <div className="flex items-center gap-[6px]">
              <div
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    aiStatus.status === "connected" ? "rgb(var(--color-success))" : "rgb(var(--color-danger))",
                }}
              />
              <span className="font-mono text-[9px] text-muted">{t.aiStatus}</span>
            </div>
          )}
          {health && (
            <div className="flex items-center gap-[6px]">
              <div
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    health.overall_status === "green"
                      ? "rgb(var(--color-success))"
                      : health.overall_status === "yellow"
                        ? "rgb(var(--color-warning))"
                        : "rgb(var(--color-danger))",
                }}
              />
              <span className="font-mono text-[9px] text-muted">{t.services}</span>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
